import api from "@/lib/api-client";
import loadImage from "blueimp-load-image";
import {
  AvatarIconType,
  IAttachment,
} from "@/features/attachments/types/attachment.types.ts";

async function compressAndResizeIcon(
  file: File,
  type: AvatarIconType,
  // Icons/avatars render tiny — 300px is plenty. Wide branding assets
  // (social preview needs 1200×630) pass a larger cap instead.
  maxSize = 300,
): Promise<File> {
  const isPng = file.type === "image/png";

  const { image: canvas } = await loadImage(file, {
    maxWidth: maxSize,
    maxHeight: maxSize,
    canvas: true,
    orientation: true,
    imageSmoothingQuality: "high",
  });

  if (type === AvatarIconType.AVATAR || !isPng) {
    const ctx = (canvas as HTMLCanvasElement).getContext("2d")!;
    ctx.globalCompositeOperation = "destination-over";
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.globalCompositeOperation = "source-over";
  }

  const outputType = isPng ? "image/png" : "image/jpeg";

  return new Promise<File>((resolve, reject) => {
    (canvas as HTMLCanvasElement).toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Failed to compress image"));
          return;
        }
        resolve(new File([blob], file.name, { type: outputType }));
      },
      outputType,
      isPng ? undefined : 0.85,
    );
  });
}

export async function uploadIcon(
  file: File,
  type: AvatarIconType,
  spaceId?: string,
  maxSize?: number,
): Promise<IAttachment> {
  // SVGs are already vector — sending them through a canvas-based resize
  // rasterises the result to JPEG/PNG bytes while keeping the .svg extension,
  // which the browser then refuses to render. Pass SVG uploads through
  // unchanged so the original markup survives.
  const isSvg =
    file.type === "image/svg+xml" || /\.svg$/i.test(file.name);
  const processed = isSvg
    ? file
    : await compressAndResizeIcon(file, type, maxSize);

  const formData = new FormData();
  formData.append("type", type);
  if (spaceId) {
    formData.append("spaceId", spaceId);
  }
  formData.append("image", processed);

  return await api.post("/attachments/upload-image", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
}

export async function uploadUserAvatar(file: File): Promise<IAttachment> {
  return uploadIcon(file, AvatarIconType.AVATAR);
}

export async function uploadSpaceIcon(
  file: File,
  spaceId: string,
): Promise<IAttachment> {
  return uploadIcon(file, AvatarIconType.SPACE_ICON, spaceId);
}

/**
 * Social-preview upload: same space-icon storage/visibility, but WITHOUT
 * the 300px icon downscale — OG images must stay 1200×630-sized or
 * Telegram/Discord render the small side-thumbnail instead of the large
 * card. Capped at 1200px wide (the OG standard) to keep weight sane.
 */
export async function uploadSpaceSocialPreview(
  file: File,
  spaceId: string,
): Promise<IAttachment> {
  return uploadIcon(file, AvatarIconType.SPACE_ICON, spaceId, 1200);
}

export async function uploadWorkspaceIcon(file: File): Promise<IAttachment> {
  return uploadIcon(file, AvatarIconType.WORKSPACE_ICON);
}

async function removeIcon(
  type: AvatarIconType,
  spaceId?: string,
): Promise<void> {
  const payload: { spaceId?: string; type: string } = { type };

  if (spaceId) {
    payload.spaceId = spaceId;
  }

  await api.post("/attachments/remove-icon", payload);
}

export async function removeAvatar(): Promise<void> {
  await removeIcon(AvatarIconType.AVATAR);
}

export async function removeSpaceIcon(spaceId: string): Promise<void> {
  await removeIcon(AvatarIconType.SPACE_ICON, spaceId);
}

export async function removeWorkspaceIcon(): Promise<void> {
  await removeIcon(AvatarIconType.WORKSPACE_ICON);
}
